import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Cpu, Gauge } from "lucide-react";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import { studioApi, useStudio } from "./studioApi";

export default function StudioLeitstand() {
  const { t, fmtDate } = useStudio();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { studioApi.leitstand().then((r) => setData(r.data)).catch((e) => setError(e)); }, []);

  if (error) return <Card className="p-8 max-w-xl" data-testid="studio-leitstand-error"><p role="alert" className="text-sm text-red-200">{t("Leitstand konnte nicht geladen werden.")}</p></Card>;
  if (!data) return <Spinner />;

  return (
    <div className="fade-in" data-testid="studio-leitstand-page">
      <Link to="/admin/studio" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-3" data-testid="studio-leitstand-back"><ArrowLeft className="w-4 h-4" /> {t("Alle Vorgänge")}</Link>
      <PageHeader title={t("Produktionsleitstand")} subtitle={t("Phase, Werkzeug, Gerät, QA, Kosten und nächste Aktion je Vorgang.")} />

      {data.errors_global.length > 0 && (
        <Card className="p-4 mb-5 border-amber-400/25" data-testid="studio-leitstand-global-errors">
          {data.errors_global.map((e, i) => <p key={i} className="text-sm text-amber-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {t(e)}</p>)}
        </Card>
      )}

      {data.rows.length === 0 ? <Empty text={t("Noch keine Studio-Vorgänge.")} /> : (
        <div className="space-y-4 stagger">
          {data.rows.map((row) => (
            <Card key={row.order_id} className="p-5" data-testid={`leitstand-row-${row.order_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <Link to={`/admin/studio/${row.order_id}`} className="font-bold hover:underline break-words">{row.company}</Link>
                  <p className="text-xs text-gray-400 mt-0.5">{t(row.phase_label)} · {row.role} · {row.tool}</p>
                </div>
                <span className="text-[11px] px-2.5 py-1 rounded-full border font-semibold bg-white/5 text-gray-300 border-white/10 inline-flex items-center gap-1.5 shrink-0" data-testid={`leitstand-device-${row.order_id}`}>
                  <Cpu className="w-3.5 h-3.5" /> {row.device || t("kein Gerät")} ({t(row.device_state)})
                </span>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mt-3 text-xs">
                <div><div className="text-gray-500 uppercase tracking-wider">{t("Letzter bestätigter Kontakt")}</div><div className="mt-0.5">{fmtDate(row.last_confirmed_contact)}</div></div>
                <div><div className="text-gray-500 uppercase tracking-wider">{t("Kosten")}</div><div className="mt-0.5">{t(row.cost.note)}</div></div>
                <div><div className="text-gray-500 uppercase tracking-wider">{t("Auftragslimit/Tag")}</div><div className="mt-0.5">{row.jobs_limit.max_jobs_per_day ?? "–"} ({t(row.jobs_limit.enforced ? "durchgesetzt" : "nicht durchgesetzt")})</div></div>
              </div>
              {row.errors.length > 0 && (
                <div className="mt-3 space-y-1" data-testid={`leitstand-errors-${row.order_id}`}>
                  {row.errors.map((e, i) => <p key={i} className="text-sm text-amber-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {t(e)}</p>)}
                </div>
              )}
              <p className="text-sm mt-3 text-[#A3E635] font-semibold flex items-center gap-2" data-testid={`leitstand-next-action-${row.order_id}`}><Gauge className="w-4 h-4" /> {t(row.next_action)}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
